import * as bootstrap from 'bootstrap';
import * as Highcharts from 'highcharts';
import { getChartTheme } from './utils/chartTheme';

// ── Globals declared in @layout.latte / Ask/default.latte ────────────────────
// Phase 181: `declare const theme` removed — renderAskChart() now calls getChartTheme() inside
// (Pitfall 5: ask re-creates per query so no chart.update needed; fresh construction reads current theme)
declare const ask_data: string[];  // example questions array (page only)

// ── Module export (matches house shape: correlations.ts lines 7-11) ──────────
export default class {
    static init = () => {
        initAsk();
    };
}

// ── Domain color map (mirrors correlations.ts DOMAIN_COLORS) ─────────────────
const DOMAIN_COLORS: Record<string, string> = {
    alcohol:      '#3BABD3',
    weight:       '#BB72F4',
    runs:         '#0090FF',
    spending:     '#FF5733',
    gym:          '#28a745',
    reading:      '#FFC107',
    manga:        '#E91E63',
    watch_time:   '#9C27B0',
    stairmaster:  '#FF9800',
    scrobbles:    '#E53935',
    mood:         '#F9C74F',
};

// ── Idempotency guard (feedback_shared_component_every_page_init_double_bind) ─
let askInitialised = false;

function initAsk(): void {
    if (askInitialised) return;
    askInitialised = true;

    // Cmd-K modal is global — wire it on every page
    setupCmdK();

    // /ask page extras — guard on the page-specific input element
    const pageInput = document.getElementById('ask-input') as HTMLInputElement | null;
    if (pageInput) {
        setupAskPage(pageInput);
    }
}

// ── Cmd-K modal setup ────────────────────────────────────────────────────────

function setupCmdK(): void {
    const modalEl = document.getElementById('ask-modal');
    if (!modalEl) return;  // host-only guard

    // Create modal instance once
    const modal = new bootstrap.Modal(modalEl);

    // Cmd-K / Ctrl-K listener (feedback_shared_component_every_page_init_double_bind)
    document.addEventListener('keydown', (e: KeyboardEvent) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
            e.preventDefault();
            modal.show();
        }
    });

    // Mobile header trigger
    const mobileTrigger = document.getElementById('ask-mobile-trigger');
    mobileTrigger?.addEventListener('click', () => modal.show());

    // Clear state when modal opens
    modalEl.addEventListener('shown.bs.modal', () => {
        const input = document.getElementById('ask-modal-input') as HTMLInputElement | null;
        input?.focus();
    });
    modalEl.addEventListener('hidden.bs.modal', () => {
        resetModalState();
    });

    // Wire modal submission
    const modalInput   = document.getElementById('ask-modal-input') as HTMLInputElement | null;
    const modalSubmit  = document.getElementById('ask-modal-submit') as HTMLButtonElement | null;
    const askUrl       = getAskUrl();

    if (modalInput && modalSubmit && askUrl) {
        modalSubmit.addEventListener('click', () => {
            const q = modalInput.value.trim();
            if (!q) return;
            submitQuestion(q, askUrl, 'modal');
        });

        modalInput.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                const q = modalInput.value.trim();
                if (!q) return;
                submitQuestion(q, askUrl, 'modal');
            }
        });
    }

    // Example pill clicks in modal (fill + auto-submit)
    modalEl.querySelectorAll('.ask-modal-example').forEach((btn) => {
        btn.addEventListener('click', () => {
            const text = (btn as HTMLElement).textContent?.trim() ?? '';
            if (modalInput) modalInput.value = text;
            if (askUrl) submitQuestion(text, askUrl, 'modal');
        });
    });
}

// ── /ask page setup ───────────────────────────────────────────────────────────

function setupAskPage(pageInput: HTMLInputElement): void {
    const submitBtn = document.getElementById('ask-submit') as HTMLButtonElement | null;
    const resetBtn  = document.getElementById('ask-reset') as HTMLButtonElement | null;

    // Signal URL is bound via data-ask-url on the input-group's parent div
    // (feedback_embedded_widget_signal_same_presenter + feedback_nette_route_and_signal_link_probes_miss)
    const askUrl = getAskUrl();

    if (!askUrl) return;

    // Submit on button click
    submitBtn?.addEventListener('click', () => {
        const q = pageInput.value.trim();
        if (!q) return;
        submitQuestion(q, askUrl, 'page');
    });

    // Submit on Enter
    pageInput.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const q = pageInput.value.trim();
            if (!q) return;
            submitQuestion(q, askUrl, 'page');
        }
    });

    // Example pill clicks (fill + auto-submit)
    document.querySelectorAll('.ask-example').forEach((btn) => {
        btn.addEventListener('click', () => {
            const text = (btn as HTMLElement).textContent?.trim() ?? '';
            pageInput.value = text;
            submitQuestion(text, askUrl, 'page');
        });
    });

    // Recent question clicks (fill + auto-submit)
    document.querySelectorAll('.ask-recent').forEach((li) => {
        li.addEventListener('click', () => {
            const q = (li as HTMLElement).dataset.q ?? '';
            if (!q) return;
            pageInput.value = q;
            submitQuestion(q, askUrl, 'page');
        });
    });

    // "Ask another question" reset
    resetBtn?.addEventListener('click', () => {
        pageInput.value = '';
        showPageState('empty');
    });
}

// ── URL resolution ────────────────────────────────────────────────────────────

function getAskUrl(): string | null {
    // Page: data-ask-url on the .input-group container
    const pageContainer = document.querySelector('[data-ask-url]') as HTMLElement | null;
    if (pageContainer) {
        const url = pageContainer.getAttribute('data-ask-url');
        if (url) return url;
    }
    return null;
}

// ── Question submission ───────────────────────────────────────────────────────

// Min visible loading time to prevent flash on fast responses (UI-SPEC Interaction Contract)
const MIN_LOADING_MS = 200;

function submitQuestion(question: string, askUrl: string, surface: 'page' | 'modal'): void {
    // Show loading state immediately
    if (surface === 'page') showPageState('loading');
    else showModalState('loading');

    const loadStart = Date.now();

    // POST to handleQuery! signal via XHR-marked fetch
    // (feedback_home_signals_curl_canonicalize_redirect — signals need XHR header)
    const body = new URLSearchParams();
    body.set('question', question);

    // Nette signal URL already contains ?do=askQuery in the {link handleQuery!} output
    fetch(askUrl, {
        method: 'POST',
        headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: body.toString(),
    })
    .then((res) => res.json())
    .then((data: AskResponse) => {
        const elapsed = Date.now() - loadStart;
        const delay = Math.max(0, MIN_LOADING_MS - elapsed);
        setTimeout(() => {
            if (surface === 'page') renderPageAnswer(data);
            else renderModalAnswer(data);
        }, delay);
    })
    .catch(() => {
        const elapsed = Date.now() - loadStart;
        const delay = Math.max(0, MIN_LOADING_MS - elapsed);
        setTimeout(() => {
            const degrade: AskResponse = {
                ok: false,
                answer: "Couldn't reach the analyst right now — try again in a moment.",
                operation: null,
                has_chart: false,
                has_data: false,
                chart: null,
                window: '',
            };
            if (surface === 'page') renderPageAnswer(degrade);
            else renderModalAnswer(degrade);
        }, delay);
    });
}

// ── Response type ─────────────────────────────────────────────────────────────

interface AskResponse {
    ok: boolean;
    answer: string;
    operation: string | null;
    has_chart: boolean;
    has_data: boolean;
    chart: AskChartData | null;
    window: string;
    // unsupported path also has ok:true + operation:'unsupported'
}

interface AskChartData {
    type: string;      // 'trend' | 'compare' | 'extreme'
    domain?: string;
    // trend: series = array of {x:timestamp, y:value}
    series?: Array<{x: number; y: number}>;
    // compare: period labels + two values
    labels?: string[];
    period_a?: number;
    period_b?: number;
    period_a_label?: string;
    period_b_label?: string;
    values?: number[];
    winner?: number;   // extreme: index of winner bar
}

// ── Page state management ─────────────────────────────────────────────────────

function showPageState(state: 'empty' | 'loading' | 'answer'): void {
    const empty   = document.getElementById('ask-empty');
    const loading = document.getElementById('ask-loading');
    const answer  = document.getElementById('ask-answer');
    if (empty)   empty.classList.toggle('d-none',   state !== 'empty');
    if (loading) loading.classList.toggle('d-none', state !== 'loading');
    if (answer)  answer.classList.toggle('d-none',  state !== 'answer');
}

function renderPageAnswer(data: AskResponse): void {
    const answerText = document.getElementById('ask-answer-text');
    const chartEl    = document.getElementById('ask-chart');
    const windowEl   = document.getElementById('ask-window');

    if (!answerText) return;

    // Degrade: ok:false → friendly warning card (T-167-06: no stack trace)
    if (!data.ok) {
        answerText.textContent = data.answer;
        if (chartEl) chartEl.classList.add('d-none');
        if (windowEl) windowEl.classList.add('d-none');
        // Wrap in warning alert style inline
        answerText.className = 'mb-0';
        const card = answerText.closest('.card-body');
        if (card && !card.querySelector('.alert')) {
            const alert = document.createElement('div');
            alert.className = 'alert alert-warning mb-0';
            alert.textContent = data.answer;
            answerText.replaceWith(alert);
        }
        showPageState('answer');
        return;
    }

    // Normal answer
    answerText.textContent = data.answer;
    answerText.className = 'mb-2';

    // Chart zone
    if (data.has_chart && data.chart && chartEl) {
        chartEl.classList.remove('d-none');
        renderAskChart(data.chart, data.operation ?? '', 'ask-chart');
    } else if (chartEl) {
        chartEl.classList.add('d-none');
    }

    // Window footnote
    if (windowEl) {
        if (data.window) {
            windowEl.textContent = data.window;
            windowEl.classList.remove('d-none');
        } else {
            windowEl.classList.add('d-none');
        }
    }

    showPageState('answer');
}

// ── Modal state management ────────────────────────────────────────────────────

function showModalState(state: 'empty' | 'loading' | 'answer'): void {
    const empty   = document.getElementById('ask-modal-empty');
    const loading = document.getElementById('ask-modal-loading');
    const answer  = document.getElementById('ask-modal-answer');
    if (empty)   empty.classList.toggle('d-none',   state !== 'empty');
    if (loading) loading.classList.toggle('d-none', state !== 'loading');
    if (answer)  answer.classList.toggle('d-none',  state !== 'answer');
}

function resetModalState(): void {
    showModalState('empty');
    const input = document.getElementById('ask-modal-input') as HTMLInputElement | null;
    if (input) input.value = '';
    const text = document.getElementById('ask-modal-answer-text');
    if (text) { text.textContent = ''; text.className = 'mb-2'; }
    const chartEl = document.getElementById('ask-modal-chart');
    if (chartEl) chartEl.classList.add('d-none');
    const windowEl = document.getElementById('ask-modal-window');
    if (windowEl) windowEl.classList.add('d-none');
}

function renderModalAnswer(data: AskResponse): void {
    const answerText = document.getElementById('ask-modal-answer-text');
    const chartEl    = document.getElementById('ask-modal-chart');
    const windowEl   = document.getElementById('ask-modal-window');

    if (!answerText) return;

    if (!data.ok) {
        // Degrade: render as warning alert (T-167-06: no trace surfaced)
        const container = answerText.parentElement;
        if (container) {
            let alertEl = container.querySelector('.ask-degrade-alert') as HTMLElement | null;
            if (!alertEl) {
                alertEl = document.createElement('div');
                alertEl.className = 'alert alert-warning mb-0 ask-degrade-alert';
                container.insertBefore(alertEl, answerText);
                answerText.classList.add('d-none');
            }
            alertEl.textContent = data.answer;
        }
        if (chartEl) chartEl.classList.add('d-none');
        if (windowEl) windowEl.classList.add('d-none');
        showModalState('answer');
        return;
    }

    // Remove any prior degrade alert
    const degradeAlert = document.getElementById('ask-modal-answer')?.querySelector('.ask-degrade-alert');
    if (degradeAlert) degradeAlert.remove();
    answerText.classList.remove('d-none');

    answerText.textContent = data.answer;
    answerText.className = 'mb-2';

    // Chart zone
    if (data.has_chart && data.chart && chartEl) {
        chartEl.classList.remove('d-none');
        renderAskChart(data.chart, data.operation ?? '', 'ask-modal-chart');
    } else if (chartEl) {
        chartEl.classList.add('d-none');
    }

    // Window footnote
    if (windowEl) {
        if (data.window) {
            windowEl.textContent = data.window;
            windowEl.classList.remove('d-none');
        } else {
            windowEl.classList.add('d-none');
        }
    }

    showModalState('answer');
}

// ── Operation-driven chart render ────────────────────────────────────────────
//
// CRITICAL: function name `renderAskChart` MUST differ from the data global `ask_data`.
// (feedback_js_chart_fn_global_var_name_collision — Phase 176 footgun)
//
// No per-chart time config — UTC setting is global in src/index.ts. (feedback_highcharts_useutc_default)
// `credits: { enabled: false }` on every chart.
// `chart.height: 320` on every chart.
// Theme-aware bg/fg from correlations.ts pattern. (feedback_highcharts_undefined_options — no explicit undefined)

// Phase 181: currentTheme parameter removed — getChartTheme() called fresh inside per query.
// ask re-creates charts fresh per query (Pitfall 5) so no chart.update() needed — fresh
// construction reads the current theme and accent.
function renderAskChart(chart: AskChartData, operation: string, containerId: string): void {
    const el = document.getElementById(containerId);
    if (!el || !chart) return;

    if (operation === 'trend') {
        // trend → spline line chart with datetime x-axis
        const seriesData = (chart.series ?? []).map((p) => [p.x, p.y]);
        const domain = chart.domain ?? '';
        const lineColor = DOMAIN_COLORS[domain] ?? '#3BABD3';  // D-01: identity colors fixed

        Highcharts.chart(containerId, Highcharts.merge(getChartTheme(), {
            chart: { type: 'spline', height: 320 },
            title: { text: '', style: { fontSize: '14px' } },
            xAxis: {
                type: 'datetime',
                gridLineWidth: 0.1,
            },
            yAxis: {
                title: { text: null },
                gridLineWidth: 0.1,
            },
            legend: { enabled: false },
            series: [{
                type: 'spline',
                name: domain || 'Value',
                data: seriesData,
                color: lineColor,
                connectNulls: true,
                marker: { radius: 3, symbol: 'circle' },
            }] as any,
            plotOptions: { spline: { lineWidth: 2 } },
            credits: { enabled: false },
        } as any));

    } else if (operation === 'compare') {
        // compare → 2-bar column chart (period A vs period B)
        const labels        = chart.labels ?? ['Period A', 'Period B'];
        const periodALabel  = chart.period_a_label ?? labels[0] ?? 'Period A';
        const periodBLabel  = chart.period_b_label ?? labels[1] ?? 'Period B';
        const valueA = chart.period_a ?? 0;
        const valueB = chart.period_b ?? 0;

        Highcharts.chart(containerId, Highcharts.merge(getChartTheme(), {
            chart: { type: 'column', height: 320 },
            title: { text: '', style: { fontSize: '14px' } },
            xAxis: {
                categories: [periodALabel, periodBLabel],
            },
            yAxis: {
                title: { text: null },
                gridLineWidth: 0.1,
            },
            legend: { enabled: false },
            series: [{
                type: 'column',
                name: 'Value',
                data: [
                    { y: valueA, color: '#3BABD3' },  // D-01: identity colors fixed
                    { y: valueB, color: '#BB72F4' },
                ],
            }] as any,
            plotOptions: { column: { borderWidth: 0 } },
            credits: { enabled: false },
        } as any));

    } else if (operation === 'extreme') {
        // extreme → horizontal bar chart; winner highlighted with live --ksat-accent (D-02)
        const labels  = chart.labels ?? [];
        const values  = chart.values ?? [];
        const winner  = chart.winner ?? 0;

        // D-02: read --ksat-accent at construction time; ask rebuilds fresh per query so this
        // always reflects the current accent — no updateAccentTaggedCharts registration needed.
        const accentHex = getComputedStyle(document.documentElement).getPropertyValue('--ksat-accent').trim();
        const barColors = values.map((_, i) =>
            i === winner ? accentHex : '#6c757d'
        );

        Highcharts.chart(containerId, Highcharts.merge(getChartTheme(), {
            chart: { type: 'bar', height: 320 },
            title: { text: '', style: { fontSize: '14px' } },
            xAxis: {
                categories: labels,
            },
            yAxis: {
                title: { text: null },
                gridLineWidth: 0.1,
            },
            legend: { enabled: false },
            series: [{
                type: 'bar',
                name: 'Value',
                data: values.map((v, i) => ({ y: v, color: barColors[i] })),
            }] as any,
            plotOptions: { bar: { borderWidth: 0 } },
            tooltip: { pointFormat: '<b>{point.y}</b>' },
            credits: { enabled: false },
        } as any));
    }
    // operation === 'total' | 'list' | 'unsupported' → PHP returns has_chart:false; no chart
}
